import axios from "axios";
import Constants from "../utils/constants";
import { Result, GetProps, PutProps, PostProps, DeleteProps } from "./types";

// Bridge the non-throwing `{ data, error }` service shape into a throwing
// queryFn. React Query needs the promise to reject on failure so it tracks
// the error and never caches a bad result; on success it returns `data`.
export async function asQuery<T = any>(p: Promise<Result>): Promise<T> {
  const res = await p;
  if (res.error) {
    throw new Error(
      typeof res.error === "string" ? res.error : "Request failed",
    );
  }
  return res.data as T;
}

const { REACT_APP_API_URL } = process.env;

// A trailing slash on REACT_APP_API_URL produces `host//path`, which Vercel
// 308-redirects to `host/path` *without* CORS headers — the browser then
// reports the redirect as a CORS failure. Strip trailing slashes from the
// base and leading slashes from the path so the URL is always single-slashed.
const API_BASE = (REACT_APP_API_URL || "").replace(/\/+$/, "");
const buildUrl = (path: string) => `${API_BASE}/${String(path).replace(/^\/+/, "")}`;

// Endpoints that proxy to third-party services (Trustap, Arta, etc.) can
// surface 401/403 from upstream. Those are NOT our session expiring — they
// mean the upstream API rejected the call (e.g. seller's Trustap account
// can't claim a transaction). Skip the global logout for these so users
// don't get kicked out of Boll-e for an unrelated upstream error.
const NO_LOGOUT_ON_401_PATHS = [
  "payments/claim-payout",
  "payments/start-shipping",
  "payments/track-shipment",
  "payments/complaint",
  "trustap-users/",
  "arta/",
];

const isProxiedPath = (url?: string) => {
  if (!url) return false;
  return NO_LOGOUT_ON_401_PATHS.some((p) => url.includes(p));
};

let isHandling401 = false;
axios.interceptors.response.use(
  (resp) => resp,
  (err) => {
    const requestUrl: string | undefined = err?.config?.url;
    if (
      err?.response?.status === 401 &&
      !isHandling401 &&
      !isProxiedPath(requestUrl)
    ) {
      isHandling401 = true;
      try {
        // Lazy require to avoid any circular import with the store.
        // eslint-disable-next-line @typescript-eslint/no-var-requires
        const { usePersistStore } = require("../store");
        usePersistStore.getState().logout();
        // Drop the React Query cache so the next (logged-in) user never sees
        // the previous session's cached data.
        // eslint-disable-next-line @typescript-eslint/no-var-requires
        require("../lib/queryClient").queryClient.clear();
      } catch (e) {}
      try {
        if (
          typeof window !== "undefined" &&
          window.location.pathname !== "/login"
        ) {
          // Remember where the session expired so re-login returns the user
          // here instead of dropping them on the home page.
          // eslint-disable-next-line @typescript-eslint/no-var-requires
          require("../utils/postLoginRedirect").setRedirectToCurrentLocation();
          window.location.href = "/login";
        }
      } catch (e) {}
      // Reset the flag on next tick so subsequent unrelated 401s
      // (after the redirect/reload) can still be handled.
      setTimeout(() => {
        isHandling401 = false;
      }, 1000);
    }
    return Promise.reject(err);
  }
);

export const Post = async ({
  path,
  token = "",
  body = {},
}: PostProps): Promise<Result> =>
  new Promise<Result>(async (resolve, reject) => {
    const headers: any = {};
    if (token) headers[Constants.TOKEN_NAME] = token;
    const result: Result = { data: null, error: "" };
    await axios
      .post(buildUrl(path), body, { headers })
      .then((resp: any) => {
        if (resp.status === 200) {
          result.data = resp.data;
        }
      })
      .catch((err: any) => {
        result.error =
          err?.response?.data ||
          err?.message ||
          "Network error. Please check your connection and try again.";
      });

    resolve(result);
  });

export const Get = async ({ path, token = "" }: GetProps): Promise<Result> =>
  new Promise<Result>(async (resolve, reject) => {
    const headers: any = {};
    if (token) headers[Constants.TOKEN_NAME] = token;
    const result: Result = { data: null, error: "" };
    await axios
      .get(buildUrl(path), { headers })
      .then((resp: any) => {
        if (resp.status === 200) {
          result.data = resp.data;
        }
      })
      .catch((err: any) => {
        result.error =
          err?.response?.data ||
          err?.message ||
          "Network error. Please check your connection and try again.";
      });

    resolve(result);
  });

export const Put = async ({
  path,
  token = "",
  body = {},
}: PutProps): Promise<Result> =>
  new Promise<Result>(async (resolve, reject) => {
    const headers: any = {};
    if (token) headers[Constants.TOKEN_NAME] = token;
    const result: Result = { data: null, error: "" };
    await axios
      .put(buildUrl(path), body, { headers })
      .then((resp: any) => {
        if (resp.status === 200) {
          result.data = resp.data;
        }
      })
      .catch((err: any) => {
        result.error =
          err?.response?.data ||
          err?.message ||
          "Network error. Please check your connection and try again.";
      });

    resolve(result);
  });

export const Delete = async ({
  path,
  token = "",
}: DeleteProps): Promise<Result> =>
  new Promise<Result>(async (resolve, reject) => {
    const headers: any = {};
    if (token) headers[Constants.TOKEN_NAME] = token;
    const result: Result = { data: null, error: "" };
    await axios
      .delete(buildUrl(path), { headers })
      .then((resp: any) => {
        if (resp.status === 200) {
          result.data = resp.data;
        }
      })
      .catch((err: any) => {
        result.error =
          err?.response?.data ||
          err?.message ||
          "Network error. Please check your connection and try again.";
      });

    resolve(result);
  });
